import { IGoogleLogin } from "@interfaces";
import { AppDispatch, googleLoginAction, toggleLoading } from "@redux";
import { useDispatch } from "react-redux";
import { ErrorToast } from "../toasts/ErrorToast";
import { StyledButton } from "@components";
import { useState, useEffect } from "react";
import { loginCallback } from "src/Utilities/routeHelper";
import { useLocation, useNavigate } from "react-router-dom";
import { theme } from "@utilities";
import { ClipLoaderCommon } from "@components";
import {
    clearGoogleOAuthHandoff,
    GOOGLE_OAUTH_CALLBACK_PATH,
    readGoogleOAuthHandoff,
    resolveGoogleOAuthReturn,
    startGoogleOAuthHandoff,
} from "src/Utilities/googleOAuthHandoff";

interface GoogleLoginButtonProps {
    onLoginSuccess?: () => Promise<void>;
    onBeforeLogin?: () => Promise<boolean | void>;
    preventRedirect?: boolean;
}

const GoogleLoginButton = ({
    onLoginSuccess,
    onBeforeLogin,
    preventRedirect,
}: GoogleLoginButtonProps = {}) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const hash = window.location.hash;
        if (hash.includes("access_token")) {
            // Google always returns to the login page, because that is the one
            // redirect URI already registered. When the sign-in began somewhere
            // else, hand the visitor back there with the token still in the
            // fragment and let that page's handler do the signing in — the shape
            // it already expects. Checked before anything else so the login page
            // does not consume a token meant for another page.
            const pendingHandoff = readGoogleOAuthHandoff();
            if (pendingHandoff) {
                clearGoogleOAuthHandoff();
                const destination = resolveGoogleOAuthReturn(hash, pendingHandoff);
                if (destination) {
                    // replace, not assign: the spent token must not sit in history.
                    window.location.replace(destination);
                    return;
                }
                // A state that does not match the nonce we issued is not a sign-in
                // this app started, so it is ignored rather than acted on.
                return;
            }

            const params = new URLSearchParams(hash.substring(1));
            const accessToken = params.get("access_token");

            if (accessToken && !isSubmitting) {
                setIsSubmitting(true);
                dispatch(toggleLoading(true));

                const values: IGoogleLogin = { google_access_token: accessToken };
                dispatch(googleLoginAction(values))
                    .then(async (res: any) => {
                        if (onLoginSuccess) {
                            if (res.payload?.data?.accessToken) {
                                const { ssStorage } = await import("src/Utilities/storage");
                                const tempJwt = ssStorage("tempJwt");
                                tempJwt.setItem(res.payload.data);
                            }
                            await onLoginSuccess();
                        } else if (!preventRedirect) {
                            await loginCallback(
                                dispatch,
                                res.payload,
                                navigate,
                                location,
                                (errorMessage) => {
                                    console.error(errorMessage);
                                    ErrorToast({ Message: "Google login failed" });
                                },
                                new URLSearchParams(location.search),
                            );
                        }
                        window.history.replaceState({}, document.title, window.location.pathname);
                    })
                    .catch((error: any) => {
                        console.error("Login error:", error);
                        ErrorToast({ Message: `${error}` });
                    })
                    .finally(() => {
                        setIsSubmitting(false);
                        dispatch(toggleLoading(false));
                    });
            }
        }
    }, [dispatch, navigate, location, onLoginSuccess, isSubmitting]);

    const handleButtonClick = async () => {
        if (onBeforeLogin) {
            const shouldContinue = await onBeforeLogin();
            if (shouldContinue === false) {
                return;
            }
        }

        const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
        // One fixed callback, not the page we happen to be on. Naming the current
        // page meant Google needed every page carrying this button on its
        // allow-list — including one entry per product — so starting from a tour
        // page answered redirect_uri_mismatch. The page to come back to travels
        // in `state` instead; see googleOAuthHandoff.
        const redirectUri = `${window.location.origin}${GOOGLE_OAUTH_CALLBACK_PATH}`;
        const nonce = startGoogleOAuthHandoff(window.location.pathname + window.location.search);
        const scope = "openid profile email";
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent(nonce)}&prompt=select_account`;
    };

    return (
        <StyledButton
            $borderRadius={theme.borderRadius.small}
            onClick={handleButtonClick}
            disabled={isSubmitting}
            $textColor="131214"
            type="button"
            color="#F4F6F7"
        >
            {isSubmitting ? (
                <ClipLoaderCommon size={24} color={theme.colors.highlight} margin={0} />
            ) : (
                <>
                    <img src="/images/Google.svg" alt="Google Logo" />
                    Continue with Google
                </>
            )}
        </StyledButton>
    );
};

export default GoogleLoginButton;
